import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Paper,
  Alert,
  CircularProgress,
  Button
} from '@mui/material';
import {
  Radio,
  Info
} from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { radioInfoService } from '../../services/radioInfoService';

const DJRadioInfo = () => {
  // Buscar informações da rádio
  const { data: radioInfoData, isLoading: radioInfoLoading } = useQuery({
    queryKey: ['radioInfo-active'],
    queryFn: () => radioInfoService.getActiveRadioInfo(),
  });

  const radioInfo = radioInfoData?.data?.radioInfo;

  return (
    <Box>
      <Card>
        <CardContent>
          <Box display="flex" alignItems="center" mb={3}>
            <Radio sx={{ mr: 2, color: '#FF6B35' }} />
            <Typography variant="h5" sx={{ fontFamily: 'Orbitron', fontWeight: 700, color: '#FF6B35' }}>
              Informações da Rádio
            </Typography>
          </Box>

          {radioInfoLoading ? (
            <Box display="flex" justifyContent="center" p={4}>
              <CircularProgress sx={{ color: '#FF6B35' }} />
            </Box>
          ) : radioInfo ? (
            <Box>
              <Alert severity="info" sx={{ mb: 3 }}>
                <Typography variant="body2">
                  <strong>Instruções:</strong> Use estas informações para configurar seu software de streaming (OBS, SAM Broadcaster, etc.) para transmissão ao vivo.
                </Typography>
              </Alert>

              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Paper sx={{ p: 2, bgcolor: 'rgba(255, 107, 53, 0.05)', border: '1px solid rgba(255, 107, 53, 0.2)' }}>
                    <Typography variant="h6" sx={{ color: '#FF6B35', mb: 2, display: 'flex', alignItems: 'center' }}>
                      <Info sx={{ mr: 1 }} />
                      Configurações do Servidor
                    </Typography>
                    
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" sx={{ opacity: 0.7, mb: 0.5 }}>
                        Protocolo
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600, fontFamily: 'monospace', bgcolor: 'rgba(0, 0, 0, 0.3)', p: 1, borderRadius: 1 }}>
                        {radioInfo.protocol?.toUpperCase()}
                      </Typography>
                    </Box>

                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" sx={{ opacity: 0.7, mb: 0.5 }}>
                        Servidor
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600, fontFamily: 'monospace', bgcolor: 'rgba(0, 0, 0, 0.3)', p: 1, borderRadius: 1 }}>
                        {radioInfo.server_host}
                      </Typography>
                    </Box>

                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" sx={{ opacity: 0.7, mb: 0.5 }}>
                        Porta
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600, fontFamily: 'monospace', bgcolor: 'rgba(0, 0, 0, 0.3)', p: 1, borderRadius: 1 }}>
                        {radioInfo.server_port}
                      </Typography>
                    </Box>

                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" sx={{ opacity: 0.7, mb: 0.5 }}>
                        Taxa de Bits
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600, fontFamily: 'monospace', bgcolor: 'rgba(0, 0, 0, 0.3)', p: 1, borderRadius: 1 }}>
                        {radioInfo.bitrate} kbps
                      </Typography>
                    </Box>

                    <Box>
                      <Typography variant="body2" sx={{ opacity: 0.7, mb: 0.5 }}>
                        Senha
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600, fontFamily: 'monospace', bgcolor: 'rgba(0, 0, 0, 0.3)', p: 1, borderRadius: 1 }}>
                        {radioInfo.password}
                      </Typography>
                    </Box>
                  </Paper>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Paper sx={{ p: 2, bgcolor: 'rgba(0, 212, 255, 0.05)', border: '1px solid rgba(0, 212, 255, 0.2)' }}>
                    <Typography variant="h6" sx={{ color: '#00D4FF', mb: 2, display: 'flex', alignItems: 'center' }}>
                      <Radio sx={{ mr: 1 }} />
                      Player para Blog
                    </Typography>
                    
                    {radioInfo.player_url ? (
                      <Box>
                        <Typography variant="body2" sx={{ opacity: 0.7, mb: 1 }}>
                          URL do Player:
                        </Typography>
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            fontFamily: 'monospace', 
                            bgcolor: 'rgba(0, 0, 0, 0.3)', 
                            p: 1, 
                            borderRadius: 1,
                            wordBreak: 'break-all',
                            mb: 2
                          }}
                        >
                          {radioInfo.player_url}
                        </Typography>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => navigator.clipboard.writeText(radioInfo.player_url)}
                          sx={{
                            borderColor: '#00D4FF',
                            color: '#00D4FF',
                            '&:hover': {
                              borderColor: '#00D4FF',
                              bgcolor: 'rgba(0, 212, 255, 0.1)'
                            }
                          }}
                        >
                          Copiar URL
                        </Button>
                      </Box>
                    ) : (
                      <Typography variant="body2" sx={{ opacity: 0.7, fontStyle: 'italic' }}>
                        Nenhum player configurado
                      </Typography>
                    )}
                  </Paper>
                </Grid>
              </Grid>

              <Alert severity="success" sx={{ mt: 3 }}>
                <Typography variant="body2">
                  <strong>Dica:</strong> Copie essas informações e cole no seu software de streaming. Certifique-se de que a taxa de bits e o protocolo estejam corretos.
                </Typography>
              </Alert>
            </Box>
          ) : (
            <Box textAlign="center" py={4}>
              <Radio sx={{ fontSize: 60, color: 'rgba(255, 255, 255, 0.3)', mb: 2 }} />
              <Typography variant="h6" sx={{ opacity: 0.7 }}>
                Informações da rádio não disponíveis
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.5 }}>
                Entre em contato com o administrador
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default DJRadioInfo;
